import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from '../app/api';

const initialState = {
    sickLeaves: [],
    loading: false,
    error: null,
}

export const addSickLeave = createAsyncThunk(
    'sickLeave/addSickLeave',
    async ({documentNumber, fromDate, toDate, workDaysData, company_id}, {rejectWithValue}) => {
        try {
            const response = await api.post('/sick-leave/add', {
                documentNumber, fromDate, toDate, workDaysData, company_id
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при добавяне на отпуск');
        }
    }
);

export const updateSickLeave = createAsyncThunk(
    'sickLeave/updateSickLeave',
    async ({id, documentNumber, fromDate, toDate, workDaysData, company_id}, {rejectWithValue}) => {
        try {
            const response = await api.put(`/sick-leave/update/${id}`, {
                documentNumber, fromDate, toDate, workDaysData, company_id
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при обновяване на отпуск');
        }
    }
);

export const deleteSickLeave = createAsyncThunk(
    'sickLeave/deleteSickLeave',
    async ({id, company_id}, {rejectWithValue}) => {
        try {
            const response = await api.delete(`/sick-leave/delete/${id}?company_id=${company_id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при изтриване на отпуск');
        }
    }
);

export const getSickLeaveById = createAsyncThunk(
    'sickLeave/getSickLeaveById',
    async ({id, company_id}, {rejectWithValue}) => {
        try {
            const response = await api.get(`/sick-leave/${id}?company_id=${company_id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при взимането на отпуск по id');
        }
    }
);

export const getAllSickLeaves = createAsyncThunk(
    'sickLeave/getAllSickLeaves',
    async ({company_id}, {rejectWithValue}) => {
        try {
            const response = await api.get(`/sick-leave/all?company_id=${company_id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при взимането на всички отпуски');
        }
    }
);

export const getSickLeaveByNumber = createAsyncThunk(
    'sickLeave/getSickLeaveByNumber',
    async ({documentNumber, company_id}, {rejectWithValue}) => {
        try {
            const response = await api.post('/sick-leave/by-number', {
                documentNumber, company_id
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при взимането на отпуск по номер');
        }
    }
);

const sickLeaveSlice = createSlice({
    name: 'sickLeave',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSickLeaves: (state) => {
            state.sickLeaves = [];
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(addSickLeave.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(addSickLeave.fulfilled, (state, action) => {
            state.loading = false;
            state.sickLeaves = [action.payload, ...state.sickLeaves];
            state.error = null;
        })
        .addCase(addSickLeave.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(updateSickLeave.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateSickLeave.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.sickLeaves = state.sickLeaves.map(sickLeave => 
                sickLeave.id === action.payload.id ? action.payload : sickLeave
            );
        })
        .addCase(updateSickLeave.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(deleteSickLeave.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteSickLeave.fulfilled, (state, action) => {
            state.loading = false;
            state.sickLeaves = state.sickLeaves.filter(sickLeave => sickLeave.id !== action.payload.id);
            state.error = null;
        })
        .addCase(deleteSickLeave.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getSickLeaveById.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getSickLeaveById.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            // Replace the sick leave in the array or add it if not exists
            const index = state.sickLeaves.findIndex(sl => sl.id === action.payload.id);
            if (index !== -1) {
                state.sickLeaves = state.sickLeaves.map((sl, i) => i === index ? action.payload : sl);
            } else {
                state.sickLeaves = [...state.sickLeaves, action.payload];
            }
        })
        .addCase(getSickLeaveById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getAllSickLeaves.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getAllSickLeaves.fulfilled, (state, action) => {
            state.loading = false;
            state.sickLeaves = action.payload;
            state.error = null;
        })
        .addCase(getAllSickLeaves.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getSickLeaveByNumber.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getSickLeaveByNumber.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            // This returns a summary, so we don't update the sickLeaves array
        })
        .addCase(getSickLeaveByNumber.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
    }
});

export const { clearError, clearSickLeaves } = sickLeaveSlice.actions;
export default sickLeaveSlice.reducer;